// ============================================================================
// TRAINING GUIDE CONTENT - AI-Synthesized Training Guide Display
// ============================================================================

import { useState, useEffect, useCallback } from 'react'
import { useAIStore } from '@/stores/aiStore'
import { knowledgeApi, type PMTopic, type TopicGuide, type GuideSection } from '@/lib/api/client'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  BookOpen,
  FileText,
  Compass,
  Search,
  GitBranch,
  Users,
  Rocket,
  BarChart3,
  MessageSquare,
  Code,
  Sparkles,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Lightbulb,
} from 'lucide-react'

// Map topic IDs to Lucide icons
const TOPIC_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  strategy: Compass,
  discovery: Search,
  processes: GitBranch,
  organization: Users,
  gtm: Rocket,
  metrics: BarChart3,
  stakeholders: MessageSquare,
  technical: Code,
}

// Map topic colors to Tailwind classes
const COLOR_CLASSES: Record<string, { bg: string; text: string; border: string; badge: string }> = {
  market: {
    bg: 'bg-market/10',
    text: 'text-market',
    border: 'border-market/20',
    badge: 'bg-market',
  },
  business: {
    bg: 'bg-business/10',
    text: 'text-business',
    border: 'border-business/20',
    badge: 'bg-business',
  },
  solution: {
    bg: 'bg-solution/10',
    text: 'text-solution',
    border: 'border-solution/20',
    badge: 'bg-solution',
  },
}

interface TrainingGuideContentProps {
  topic: PMTopic
  accountId: string
}

export function TrainingGuideContent({ topic, accountId }: TrainingGuideContentProps) {
  const { apiKey } = useAIStore()
  const [guide, setGuide] = useState<TopicGuide | null>(null)
  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [expandedSections, setExpandedSections] = useState<Set<number>>(new Set([0]))

  const Icon = TOPIC_ICONS[topic.id] || BookOpen
  const colors = COLOR_CLASSES[topic.color]

  // Fetch existing guide
  const fetchGuide = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await knowledgeApi.getTopicGuide(accountId, topic.id)
      setGuide(response.guide)

      // Expand first section by default
      if (response.guide?.sections.length) {
        setExpandedSections(new Set([0]))
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load guide')
    } finally {
      setLoading(false)
    }
  }, [accountId, topic.id])

  useEffect(() => {
    fetchGuide()
  }, [fetchGuide])

  // Generate new guide
  const handleGenerate = useCallback(async () => {
    if (!apiKey) {
      setError('Please configure your Claude API key in Settings to generate guides.')
      return
    }

    try {
      setGenerating(true)
      setError(null)
      const response = await knowledgeApi.generateTopicGuide(accountId, topic.id, apiKey)
      setGuide(response.guide)
      setExpandedSections(new Set([0]))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate guide')
    } finally {
      setGenerating(false)
    }
  }, [accountId, topic.id, apiKey])

  const toggleSection = (index: number) => {
    const newExpanded = new Set(expandedSections)
    if (newExpanded.has(index)) {
      newExpanded.delete(index)
    } else {
      newExpanded.add(index)
    }
    setExpandedSections(newExpanded)
  }

  // Loading state
  if (loading) {
    return (
      <div className="space-y-4">
        <TopicHeader topic={topic} Icon={Icon} colors={colors} loading />
        {[1, 2, 3].map(i => (
          <div key={i} className="bg-white rounded-xl border border-warm-200 p-5 animate-pulse">
            <div className="h-5 bg-warm-200 rounded w-1/3 mb-3"></div>
            <div className="h-4 bg-warm-100 rounded w-2/3"></div>
          </div>
        ))}
      </div>
    )
  }

  // Error state
  if (error && !guide) {
    return (
      <div className="space-y-6">
        <TopicHeader topic={topic} Icon={Icon} colors={colors} />
        <div className="text-center py-12 bg-white rounded-xl border border-warm-200">
          <AlertCircle className="w-12 h-12 text-error mx-auto mb-3" />
          <p className="text-error mb-4">{error}</p>
          <Button variant="outline" onClick={fetchGuide}>
            Retry
          </Button>
        </div>
      </div>
    )
  }

  // No guide yet - show generate prompt
  if (!guide) {
    return (
      <div className="space-y-6">
        <TopicHeader topic={topic} Icon={Icon} colors={colors} />
        <div className="text-center py-12 bg-white rounded-xl border border-warm-200">
          <div className="w-16 h-16 rounded-xl bg-market/10 flex items-center justify-center mx-auto mb-4">
            <Sparkles className="w-8 h-8 text-market" />
          </div>
          <h3 className="font-semibold text-text mb-2">Generate Training Guide</h3>
          <p className="text-text-secondary text-sm mb-2 max-w-md mx-auto">
            Create an AI-synthesized training guide with best practices, key concepts, and actionable advice based on your knowledge base.
          </p>
          {topic.chunkCount === 0 ? (
            <p className="text-text-muted text-xs mb-6">
              No content available. Add and classify knowledge sources first.
            </p>
          ) : (
            <p className="text-text-muted text-xs mb-6">
              {topic.chunkCount} knowledge chunks available for synthesis.
            </p>
          )}
          {error && (
            <p className="text-error text-sm mb-4">{error}</p>
          )}
          <Button
            onClick={handleGenerate}
            disabled={generating || topic.chunkCount === 0 || !apiKey}
            className="bg-market hover:bg-market/90 text-white"
          >
            {generating ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Generating Guide...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                Generate Guide
              </>
            )}
          </Button>
          {!apiKey && (
            <p className="text-xs text-text-muted mt-3">
              Configure Claude API key in Settings to generate guides.
            </p>
          )}
        </div>
      </div>
    )
  }

  // Display the synthesized guide
  return (
    <div className="space-y-6">
      <TopicHeader
        topic={topic}
        Icon={Icon}
        colors={colors}
        guide={guide}
        onRegenerate={handleGenerate}
        isRegenerating={generating}
      />

      {/* Overview */}
      {guide.overview && (
        <Card className="overflow-hidden">
          <CardContent className="p-6">
            <p className="text-text leading-relaxed">{guide.overview}</p>
          </CardContent>
        </Card>
      )}

      {/* Error banner */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-sm text-error">
          <AlertCircle className="w-4 h-4" />
          {error}
        </div>
      )}

      {/* Guide Sections */}
      <div className="space-y-4">
        {guide.sections.map((section, index) => {
          const isExpanded = expandedSections.has(index)
          return (
            <Card key={index} className="overflow-hidden">
              <button
                onClick={() => toggleSection(index)}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-warm-50 transition-colors text-left"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg ${colors.bg} flex items-center justify-center text-sm font-semibold ${colors.text}`}>
                    {index + 1}
                  </div>
                  <h3 className="font-semibold text-text">{section.title}</h3>
                </div>
                {isExpanded ? (
                  <ChevronUp className="w-5 h-5 text-text-muted" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-text-muted" />
                )}
              </button>

              {isExpanded && (
                <CardContent className="pt-0 pb-6 px-6">
                  <div className="border-t border-warm-200 pt-4">
                    {/* Section Content */}
                    <div className="prose prose-sm max-w-none text-text leading-relaxed whitespace-pre-wrap">
                      {section.content}
                    </div>

                    {/* Key Points */}
                    {section.keyPoints.length > 0 && (
                      <div className="mt-6 bg-warm-50 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <Lightbulb className={`w-4 h-4 ${colors.text}`} />
                          <span className={`text-sm font-semibold ${colors.text}`}>Key Points</span>
                        </div>
                        <ul className="space-y-2">
                          {section.keyPoints.map((point, pointIndex) => (
                            <li key={pointIndex} className="flex items-start gap-2 text-sm text-text">
                              <CheckCircle2 className={`w-4 h-4 mt-0.5 flex-shrink-0 ${colors.text}`} />
                              <span>{point}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </CardContent>
              )}
            </Card>
          )
        })}
      </div>

      {/* Guide metadata */}
      <div className="text-center text-xs text-text-muted pt-4">
        Generated from {guide.sourceChunkCount} knowledge chunks
        {guide.isStale && (
          <span className="ml-2 text-amber-600">
            (Content has changed - consider regenerating)
          </span>
        )}
      </div>
    </div>
  )
}

interface TopicHeaderProps {
  topic: PMTopic
  Icon: React.ComponentType<{ className?: string }>
  colors: { bg: string; text: string; border: string; badge: string }
  loading?: boolean
  guide?: TopicGuide | null
  onRegenerate?: () => void
  isRegenerating?: boolean
}

function TopicHeader({
  topic,
  Icon,
  colors,
  loading,
  guide,
  onRegenerate,
  isRegenerating,
}: TopicHeaderProps) {
  return (
    <div className={`${colors.bg} rounded-xl p-6 border ${colors.border}`}>
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 rounded-lg ${colors.badge} flex items-center justify-center`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <div className="flex items-start justify-between">
            <div>
              <h1 className={`text-xl font-bold ${colors.text}`}>
                {guide?.title || topic.name}
              </h1>
              <p className="text-text-secondary mt-1">{topic.description}</p>
            </div>
            {guide && onRegenerate && (
              <Button
                variant="outline"
                size="sm"
                onClick={onRegenerate}
                disabled={isRegenerating}
                className={`${colors.text} border-current hover:bg-white/50`}
              >
                {isRegenerating ? (
                  <>
                    <RefreshCw className="w-3 h-3 mr-1.5 animate-spin" />
                    Regenerating...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-3 h-3 mr-1.5" />
                    Regenerate
                  </>
                )}
              </Button>
            )}
          </div>
          {!loading && (
            <div className="flex items-center gap-4 mt-3">
              {guide ? (
                <>
                  <Badge variant="outline" className={`text-xs ${colors.text} border-current`}>
                    <Sparkles className="w-3 h-3 mr-1" />
                    AI Guide v{guide.version}
                  </Badge>
                  <span className="text-xs text-text-muted">
                    {guide.sections.length} sections
                  </span>
                  {guide.isStale && (
                    <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">
                      Needs refresh
                    </Badge>
                  )}
                </>
              ) : (
                <span className="text-xs text-text-muted">
                  {topic.chunkCount} knowledge chunks available
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default TrainingGuideContent
